import * as echarts from "echarts";
import { useEffect, useMemo, useRef } from "react";

const DEFAULT_TIME_ZONE = "America/Argentina/Buenos_Aires";

const formatDateTime = (value, timeZone = DEFAULT_TIME_ZONE) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("es-AR", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
};

const formatTime = (value, timeZone = DEFAULT_TIME_ZONE) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleTimeString("es-AR", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
};

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const getPeriodLabel = (periodType) => {
  switch (periodType) {
    case "PRODUCTIVO":
      return "Horario productivo";
    case "NO_PRODUCTIVO":
      return "Fuera de horario";
    case "PRE_OPERATIVO":
      return "Pre operativo";
    case "POST_OPERATIVO":
      return "Post operativo";
    case "HORA_EXTRA":
      return "Hora extra";
    case "SUGGESTED_OVERTIME":
      return "Posible hora extra";
    case "MIXTO":
      return "Mixto";
    default:
      return periodType || "-";
  }
};

const getPointDeviceLabel = (point) => {
  return (
    point?.device_label ||
    point?.device_name ||
    point?.name ||
    point?.label ||
    point?.device_mac ||
    point?.mac ||
    null
  );
};

const getGroupedPeriodType = (periodTypes) => {
  const unique = [...new Set(periodTypes.filter(Boolean))];

  if (unique.length === 0) return "-";
  if (unique.length === 1) return unique[0];

  if (unique.includes("NO_PRODUCTIVO")) return "NO_PRODUCTIVO";
  if (unique.includes("PRE_OPERATIVO")) return "PRE_OPERATIVO";
  if (unique.includes("POST_OPERATIVO")) return "POST_OPERATIVO";
  if (unique.includes("HORA_EXTRA")) return "HORA_EXTRA";
  if (unique.includes("PRODUCTIVO")) return "PRODUCTIVO";

  return "MIXTO";
};

const summarizeDevices = (deviceLabels) => {
  const unique = [...new Set(deviceLabels.filter(Boolean))];

  if (unique.length === 0) return "-";
  if (unique.length === 1) return unique[0];

  return `${unique.length} dispositivos`;
};

const groupSeriesByTimestamp = (series = []) => {
  const grouped = new Map();

  series.forEach((point) => {
    if (!point?.timestamp) return;

    const key = point.timestamp;

    if (!grouped.has(key)) {
      grouped.set(key, {
        timestamp: key,
        local_timestamp: point.local_timestamp,
        local_time: point.local_time,

        total_energy: 0,
        cost_total: 0,

        periodTypes: [],
        reasons: [],
        deviceLabels: [],
        devices: [],

        is_non_productive: false,
        anomalyLevels: [],
      });
    }

    const item = grouped.get(key);

    item.total_energy += toNumber(point.total_energy);
    item.cost_total += toNumber(point.cost_total);

    if (point.period_type) item.periodTypes.push(point.period_type);
    if (point.passive_reason) item.reasons.push(point.passive_reason);
    if (point.anomaly_level) item.anomalyLevels.push(point.anomaly_level);

    const deviceLabel = getPointDeviceLabel(point);

    if (deviceLabel) {
      item.deviceLabels.push(deviceLabel);

      item.devices.push({
        label: deviceLabel,
        mac: point.device_mac || point.mac || null,
        total_energy: toNumber(point.total_energy),
        period_type: point.period_type,
        passive_reason: point.passive_reason,
      });
    }

    item.is_non_productive =
      item.is_non_productive || point.is_non_productive === true;
  });

  return Array.from(grouped.values())
    .map((item) => {
      const periodType = getGroupedPeriodType(item.periodTypes);
      const uniqueReasons = [...new Set(item.reasons.filter(Boolean))];
      const uniqueDevices = [...new Set(item.deviceLabels.filter(Boolean))];

      return {
        ...item,
        period_type: periodType,
        passive_reason:
          uniqueReasons.length === 1
            ? uniqueReasons[0]
            : uniqueReasons.length > 1
              ? "Ver detalle por dispositivo"
              : "-",
        device_label: summarizeDevices(uniqueDevices),
        device_count: uniqueDevices.length,
        device_names: uniqueDevices,
        total_energy: Number(item.total_energy.toFixed(6)),
        cost_total: Number(item.cost_total.toFixed(2)),
      };
    })
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
};

const findPointByTimestamp = (series, timestamp) => {
  if (!timestamp) return null;

  const exact = series.find((point) => point.timestamp === timestamp);

  if (exact) return exact;

  const targetMs = new Date(timestamp).getTime();

  if (!Number.isFinite(targetMs)) return null;

  let closestPoint = null;
  let closestDiff = Infinity;

  series.forEach((point) => {
    const pointMs = new Date(point?.timestamp).getTime();

    if (!Number.isFinite(pointMs)) return;

    const diff = Math.abs(pointMs - targetMs);

    if (diff < closestDiff) {
      closestDiff = diff;
      closestPoint = point;
    }
  });

  return closestPoint;
};

const buildDevicesTooltip = (devices = []) => {
  if (!Array.isArray(devices) || devices.length <= 1) return "";

  const rows = devices
    .map((device) => {
      return `
        <div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px;">
          <span>${device.label}</span>
          <strong>${toNumber(device.total_energy).toFixed(3)} kWh</strong>
        </div>
      `;
    })
    .join("");

  return `
    <div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(226,232,240,0.18);">
      <div style="font-weight:700;margin-bottom:4px;">Detalle por dispositivo</div>
      ${rows}
    </div>
  `;
};

const PassiveBaseLoadChart = ({ data }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const timeZone = data?.timeZone || DEFAULT_TIME_ZONE;

  const groupedSeries = useMemo(() => {
    const sourceSeries = Array.isArray(data?.series) ? data.series : [];

    return groupSeriesByTimestamp(sourceSeries);
  }, [data?.series]);

  useEffect(() => {
    if (!chartRef.current) return;

    chartInstance.current = echarts.init(chartRef.current);

    const resize = () => chartInstance.current?.resize();
    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
      chartInstance.current?.dispose();
    };
  }, []);

  useEffect(() => {
    if (!chartInstance.current) return;

    if (!groupedSeries.length) {
      chartInstance.current.clear();
      return;
    }

    const totalSeries = groupedSeries.map((point) => [
      point.timestamp,
      toNumber(point.total_energy),
    ]);

    const rawBaseLoad =
      data?.baseLoad ??
      data?.totals?.baseLoad ??
      data?.totals?.baseLoadEnergy ??
      null;

    const baseLoadValue = Number(rawBaseLoad);

    const baseLoadUnit = "kWh";

    const hasBaseLoad = Number.isFinite(baseLoadValue);

    const baseLoadLabel = hasBaseLoad
      ? `Carga base estimada: ${baseLoadValue.toFixed(3)} ${baseLoadUnit}`
      : "Carga base estimada no disponible";

    const option = {
      backgroundColor: "#ffffff",
      animation: true,
      grid: {
        left: 56,
        right: 96,
        top: 76,
        bottom: 52,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        confine: true,
        appendToBody: true,
        axisPointer: {
          type: "line",
          lineStyle: {
            color: "#64748b",
            width: 1,
            type: "dashed",
          },
        },
        backgroundColor: "rgba(15, 23, 42, 0.92)",
        borderColor: "rgba(15, 23, 42, 0.92)",
        textStyle: {
          color: "#ffffff",
          fontSize: 12,
        },
        extraCssText:
          "box-shadow: 0 10px 24px rgba(15,23,42,0.18); border-radius: 10px; padding: 10px 12px; max-width: 520px;",
        formatter: (params) => {
          const rows = Array.isArray(params) ? params : [];
          const first = rows[0];

          if (!first) return "";

          const timestamp = first.axisValue;
          const point = findPointByTimestamp(groupedSeries, timestamp);

          const totalEnergy = toNumber(point?.total_energy);
          const periodLabel = getPeriodLabel(point?.period_type);
          const reason = point?.passive_reason;
          const deviceLabel = point?.device_label || "-";

          return `
            <div style="min-width:260px;">
              <div style="font-weight:700;margin-bottom:6px;">
                ${formatDateTime(timestamp, timeZone)}
              </div>

              <div style="margin-bottom:4px;">
                Dispositivos: <strong>${deviceLabel}</strong>
              </div>

              <div style="margin-bottom:4px;">
                Período: <strong>${periodLabel}</strong>
              </div>

              ${
                reason && reason !== "-"
                  ? `<div style="margin-bottom:6px;color:#cbd5e1;">Motivo: ${reason}</div>`
                  : ""
              }

              <div style="display:flex;justify-content:space-between;gap:16px;margin-top:4px;">
                <span>${first.marker} Consumo real total</span>
                <strong>${totalEnergy.toFixed(3)} kWh</strong>
              </div>

              ${
                hasBaseLoad
                  ? `
                    <div style="display:flex;justify-content:space-between;gap:16px;margin-top:4px;">
                      <span>Carga base estimada</span>
                      <strong>${baseLoadValue.toFixed(3)} ${baseLoadUnit}</strong>
                    </div>
                  `
                  : ""
              }

              ${buildDevicesTooltip(point?.devices)}
            </div>
          `;
        },
      },
      legend: {
        top: 12,
        left: "center",
        type: "scroll",
        itemWidth: 12,
        itemHeight: 8,
        itemGap: 18,
        textStyle: {
          color: "#334155",
          fontSize: 12,
        },
        data: ["Consumo real total"],
      },
      xAxis: {
        type: "time",
        axisLine: {
          lineStyle: {
            color: "#cbd5e1",
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: "#64748b",
          hideOverlap: true,
          formatter: (value) => formatTime(value, timeZone),
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        name: "kWh",
        nameTextStyle: {
          color: "#64748b",
          fontSize: 11,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: "#94a3b8",
          formatter: (value) => Number(value).toFixed(2),
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: "#e2e8f0",
            type: "solid",
          },
        },
      },
      series: [
        {
          name: "Consumo real total",
          type: "bar",
          data: totalSeries,
          barMaxWidth: 18,
          itemStyle: {
            color: "rgba(148, 163, 184, 0.55)",
            borderRadius: [3, 3, 0, 0],
          },
          emphasis: {
            itemStyle: {
              color: "rgba(100, 116, 139, 0.75)",
            },
          },
          z: 3,
          markLine: hasBaseLoad
            ? {
                silent: false,
                symbol: "none",
                lineStyle: {
                  color: "#3b5bdb",
                  width: 2,
                  type: "dashed",
                },
                label: {
                  show: true,
                  position: "insideEndTop",
                  formatter: baseLoadLabel,
                  color: "#3b5bdb",
                  fontSize: 11,
                  fontWeight: 600,
                  distance: 10,
                  backgroundColor: "rgba(255,255,255,0.96)",
                  borderColor: "rgba(59,91,219,0.22)",
                  borderWidth: 1,
                  borderRadius: 6,
                  padding: [4, 6],
                },
                emphasis: {
                  lineStyle: {
                    color: "#2f49b5",
                    width: 2,
                  },
                },
                data: [
                  {
                    name: "Carga base estimada",
                    yAxis: baseLoadValue,
                  },
                ],
              }
            : undefined,
        },
      ],
    };

    chartInstance.current.setOption(option, true);
  }, [data, groupedSeries, timeZone]);

  return <div ref={chartRef} style={{ width: "100%", height: "380px" }} />;
};

export default PassiveBaseLoadChart;